/**
 * The workbench's own classes. Everything structural comes from the menu kit
 * (`.mm-card`, `.metabtn`, the `--mm-*` tokens); this file is only the three
 * shapes the menu does not already have — the viewer's frame, an assembly row
 * and a part option. Prefix `.wb-`.
 */
export const WORKBENCH_CSS = `
.wb {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  height: 100%;
  min-height: 0;
}
/* The bar across the top: the way back, what is on the bench, the verdict and
   the one button that spends money or turns a wrench. */
.wb-top {
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 0 0 auto;
}
.wb-top-right {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}
.wb-summary { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; }
.wb-body {
  display: flex;
  gap: 14px;
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 1600px;
}
/* The turntable's frame: the same hatched dark box the floating bench uses, so
   a gun looks the same on either. */
.wb-viewer {
  position: relative;
  overflow: hidden;
  border-radius: var(--mm-radius-sm);
  border: 1px solid var(--mm-line);
  background:
    repeating-linear-gradient(135deg, rgba(255,255,255,.015) 0 3px, transparent 3px 9px),
    #080a0b;
}
/* One mounting point. Indent by depth, so a part's own slots read as its own. */
.wb-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 8px;
  box-sizing: border-box;
  border: 1px solid transparent;
  border-radius: var(--mm-radius-sm);
  background: transparent;
  color: var(--mm-text);
  font: 12.5px/1.2 var(--mm-sans);
  text-align: left;
  cursor: pointer;
  transition: background var(--mm-ease), border-color var(--mm-ease);
}
.wb-row:hover { background: var(--mm-hover); }
.wb-row[aria-selected="true"] {
  background: var(--mm-accent-wash);
  border-color: rgba(214, 176, 108, 0.42);
}
.wb-row[data-depth="1"] { padding-left: 20px; }
.wb-row-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.wb-slot { flex: 0 0 108px; color: var(--mm-text-dim); font-size: 11.5px; }
/* The status badge on the right of a row, and the same words in an option. */
.wb-tag {
  flex: 0 0 auto;
  padding: 2px 7px;
  border-radius: 9px;
  border: 1px solid var(--mm-line-strong);
  font: 600 10px/1.4 var(--mm-mono);
  color: var(--mm-text-dim);
  white-space: nowrap;
}
.wb-tag[data-status="fitted"] { color: var(--mm-good); border-color: rgba(143, 211, 154, 0.4); }
.wb-tag[data-status="owned"] { color: var(--mm-text); }
.wb-tag[data-status="buy"] { color: var(--mm-accent-hi); border-color: rgba(214, 176, 108, 0.4); }
.wb-tag[data-status="unavailable"] { color: var(--mm-bad); border-color: rgba(224, 128, 118, 0.4); }
/* One candidate part: picture, name and where it would come from, deltas. */
.wb-part {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 6px;
  box-sizing: border-box;
  border: 1px solid var(--mm-line);
  border-radius: var(--mm-radius-sm);
  background: rgba(255, 255, 255, 0.02);
  color: var(--mm-text);
  font: 12.5px/1.2 var(--mm-sans);
  text-align: left;
  cursor: pointer;
  transition: background var(--mm-ease), border-color var(--mm-ease);
}
.wb-part:hover { background: var(--mm-hover); }
.wb-part[aria-selected="true"] {
  background: var(--mm-accent-wash);
  border-color: rgba(214, 176, 108, 0.52);
}
.wb-thumb {
  position: relative;
  flex: 0 0 auto;
  width: 56px;
  height: 34px;
  border-radius: var(--mm-radius-sm);
  border: 1px solid var(--mm-line);
  background: rgba(255, 255, 255, 0.03);
  overflow: hidden;
}
.wb-part-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
.wb-part-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.wb-deltas { display: flex; flex-wrap: wrap; gap: 6px; }
.wb-delta { font: 10.5px/1.3 var(--mm-mono); white-space: nowrap; }
/* The base → planned table under the viewer. */
.wb-stat {
  display: grid;
  grid-template-columns: 130px 1fr 16px 1fr;
  gap: 8px;
  align-items: center;
  font-size: 11.5px;
  padding: 2px 0;
}
.wb-stat-arrow { color: var(--mm-text-faint); text-align: center; }
`;
